import * as React from 'react'

import {
  ProductImageUrls,
  ProductPriceProps,
  ProductButton,
  ProductPrice,
} from '@thg-commerce/gravity-elements'
import { ProductQuantityProps } from '@thg-commerce/gravity-elements/Product/ProductQuantity/ProductQuantity'
import { ProductReviewProps } from '@thg-commerce/gravity-elements/Product/ProductReview/ProductReview'
import { BreakpointArray } from '@thg-commerce/gravity-theme'
import { useTheme } from '@thg-commerce/gravity-patterns/theme'
import { MarketedSpecialOfferSummaryContainerProps } from '@thg-commerce/gravity-patterns/MarketedSpecialOfferSummary'
import { SwatchProps } from '@thg-commerce/gravity-elements/Swatch'
import {
  ProductOptionsProps,
  ProductOptions,
} from '@thg-commerce/gravity-patterns/ProductOptions'
import { PictureProps } from '@thg-commerce/gravity-system'

import { Directions, FlexAlignments, HorizontalAlignment } from './types'
import {
  Container,
  ImageContainer,
  StyledImage,
  StyledHoverImage,
  StyledHoverImageContainer,
  AttributesContainer,
  Brand,
  Title,
  StyledProductReview,
  StyledProductQuantity,
  Description,
  IconsAndPriceWrapper,
  IconsWrapper,
  StyledButton,
  StyledMarketedSpecialOfferSummaryContainer,
  StyledSwatch,
  TitleWrapper,
  ProductOptionsContainer,
  ButtonContainer,
  PowerReviewCategorySnippetContainer,
} from './styles'
import { ProductBlockPicture } from './ProductBlockPicture'

export interface ProductBlockTitleProps {
  value: string
  hideLabel?: boolean
  useAlternateStyle?: boolean
}

interface ProductBlockButtonProps {
  title: string
  ariaLabel?: string
  disabled?: boolean
  onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void
  dataTestId?: string
  disableHref?: boolean
}

interface ProductBlockAttributesProps {
  justifyContent?: FlexAlignments
  alignItems?: FlexAlignments
  gap?: BreakpointArray<number>
}

export interface ProductBlockImageProps {
  isAmp: boolean
  lazy?: boolean
  urls: ProductImageUrls
  altText?: string
  maxWidths?: string[]
  width?: number | string | number[] | string[]
  height?: number | string | number[] | string[]
  fetchPriority?: 'low' | 'high' | 'auto'
}

export interface ProductBlockProps {
  title: ProductBlockTitleProps
  brand?: {
    name: string
  }
  url: string
  review?: ProductReviewProps | null
  price?: ProductPriceProps
  description?: string
  image: ProductBlockImageProps
  annotateImageComponent?: React.ComponentType<{
    children: React.ReactNode
  }>
  powerReviewCategorySnippetComponent?: React.ComponentType<{}>
  picture?: PictureProps
  hoverPicture?: PictureProps
  marketedSpecialOffer?: MarketedSpecialOfferSummaryContainerProps
  hoverImage?: ProductBlockImageProps
  button?: ProductBlockButtonProps
  options?: ProductOptionsProps
  quantity?: ProductQuantityProps
  attributes?: ProductBlockAttributesProps
  condensed?: boolean
  directions?: Directions
  reversePriceAndQuantity?: boolean
  swatch?: SwatchProps
  icons?: React.ReactNode
  className?: string
  titleAlignment?: HorizontalAlignment
  onClickEventEmitter?: () => void
  showBrandTitle?: boolean
  hideProductListSwatch?: boolean
  attributesInsetSpacing?: BreakpointArray<number>
  externalIndentifier?: string
  showPowerReview?: boolean
  disableUrl?: boolean
}

export const ProductBlock = ({
  image,
  annotateImageComponent,
  hoverImage,
  url,
  title: {
    value: titleValue,
    hideLabel: hideTitleLabel = false,
    useAlternateStyle = false,
  },
  brand,
  marketedSpecialOffer,
  price,
  description,
  review,
  button,
  options,
  quantity,
  attributes,
  condensed = false,
  directions = ['column'],
  reversePriceAndQuantity = false,
  swatch,
  icons,
  className,
  onClickEventEmitter,
  titleAlignment,
  showBrandTitle = false,
  hideProductListSwatch = false,
  attributesInsetSpacing,
  picture,
  hoverPicture,
  powerReviewCategorySnippetComponent,
  showPowerReview,
  disableUrl = false,
}: ProductBlockProps) => {
  const theme = useTheme()
  const priceAndQuantity = [
    ...(price ? [<ProductPrice {...price} key="productblock-price" />] : []),
    ...(quantity
      ? [<StyledProductQuantity {...quantity} key="productblock-quantity" />]
      : []),
  ]

  const AnnotateImageContainer = annotateImageComponent ?? React.Fragment
  const PowerReviewCategorySnippetComponent =
    powerReviewCategorySnippetComponent ?? React.Fragment

  return (
    <Container
      {...{
        directions,
        condensed,
      }}
      className={className}
      as={!button ? 'a' : undefined}
      href={!button ? url : undefined}
      title={!button ? titleValue : undefined}
      onClick={onClickEventEmitter}
    >
      <AnnotateImageContainer>
        {picture ? (
          <ProductBlockPicture
            url={button ? url : undefined}
            picture={picture}
            hoverPicture={hoverPicture}
          />
        ) : hoverImage ? (
          <ImageContainer
            as={button ? 'a' : undefined}
            href={button ? url : undefined}
          >
            <StyledImage
              {...image}
              alt={image.altText || titleValue}
              onClick={button ? () => window.location.assign(url) : undefined}
            />
            <StyledHoverImageContainer>
              <StyledHoverImage
                {...hoverImage}
                lazy={hoverImage.lazy ?? true}
                alt={hoverImage.altText || titleValue}
                onClick={button ? () => window.location.assign(url) : undefined}
              />
            </StyledHoverImageContainer>
          </ImageContainer>
        ) : (
          <a
            role="img"
            aria-label={button ? image.altText || titleValue : undefined}
            href={button && !disableUrl ? url : undefined}
          >
            <StyledImage
              {...image}
              alt={image.altText || titleValue}
              onClick={
                button && !disableUrl
                  ? () => window.location.assign(url)
                  : undefined
              }
            />
          </a>
        )}
      </AnnotateImageContainer>
      <AttributesContainer
        {...attributes}
        directions={directions}
        attributesInsetSpacing={attributesInsetSpacing}
      >
        {!hideTitleLabel && button ? (
          <StyledButton
            sizing="regular"
            emphasis="low"
            renderedAs={!disableUrl ? 'a' : undefined}
            href={url}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {brand?.name && showBrandTitle && (
                <Brand
                  useAlternateStyle={useAlternateStyle}
                  alignment={titleAlignment}
                >
                  {brand.name}
                </Brand>
              )}
              <Title
                useAlternateStyle={useAlternateStyle}
                alignment={titleAlignment}
              >
                {titleValue}
              </Title>
            </div>
          </StyledButton>
        ) : (
          <TitleWrapper>
            {brand?.name && showBrandTitle && (
              <Brand
                useAlternateStyle={useAlternateStyle}
                alignment={titleAlignment}
              >
                {brand.name}
              </Brand>
            )}
            <Title useAlternateStyle={false} alignment={titleAlignment}>
              {titleValue}
            </Title>
          </TitleWrapper>
        )}
        {!showPowerReview && review && review.numberOfReviews !== 0 && (
          <StyledProductReview
            {...review}
            colorFills={
              theme.patterns.productBlock.components.productReview.colorFills ||
              undefined
            }
          />
        )}
        {powerReviewCategorySnippetComponent && showPowerReview && (
          <PowerReviewCategorySnippetContainer>
            {<PowerReviewCategorySnippetComponent />}
          </PowerReviewCategorySnippetContainer>
        )}
        {marketedSpecialOffer && (
          <StyledMarketedSpecialOfferSummaryContainer
            {...marketedSpecialOffer}
          />
        )}
        {swatch && swatch.colours?.length > 0 && !hideProductListSwatch && (
          <StyledSwatch
            {...swatch}
            shape={theme.patterns.productBlock.components.swatch.shape}
            borderStyle={{
              color:
                theme.patterns.productBlock.components.swatch.border?.color,
            }}
          />
        )}
        <IconsAndPriceWrapper
          withIcons={!!icons}
          data-testid="icons-and-price-container"
        >
          {reversePriceAndQuantity
            ? priceAndQuantity.reverse()
            : priceAndQuantity}
          {icons && <IconsWrapper>{icons}</IconsWrapper>}
        </IconsAndPriceWrapper>
        {description && <Description content={description} />}
        {options && options.options.length > 0 && (
          <ProductOptionsContainer>
            <ProductOptions {...options} />
          </ProductOptionsContainer>
        )}
        {button && !disableUrl && (
          <ButtonContainer>
            {button && (
              <ProductButton
                {...button}
                href={button.disableHref ? undefined : url}
              />
            )}
          </ButtonContainer>
        )}
      </AttributesContainer>
    </Container>
  )
}
